import React, { useEffect, useMemo, useState } from 'react';
import {
  BulkActionBar, ColumnPicker, ConfirmDialog, DataTable, DateRangePicker, DensityToggle, EmptyState,
  exportFilename, exportRows, FilterBar, formatThaiDate, Pagination, SearchInput, SourceBadge,
  StatusBadge, Toolbar, useTableDensity, useToast,
  type ActiveFilter, type Column, type DateRange, type QueueStatus, type SortState,
} from '../components';
import { Icon } from '../components/base/Icon';
import { CLAIMS, CURRENT_STAFF_ID, pointsFor, type Channel, type Claim } from './mockData';

/**
 * BulkReviewScreen — อนุมัติคำขอแบบกลุ่ม (FR-ADM-13)
 *
 * ข้อควรระวังที่ออกแบบไว้ตั้งแต่แรก: PROGRESS-REPORT 3.2 อธิบายว่าสลิปต้องใช้คนตรวจ
 * "เพราะรูปสลิปแก้ไขได้และใช้ซ้ำได้" การอนุมัติเป็นกลุ่มโดยไม่เปิดดูภาพจึงลบเหตุผล
 * ของการมีคิวทิ้งไป หน้านี้จึงไม่ใช่ทางลัดของคิวปกติ แต่เป็นเครื่องมือสำหรับ
 * "คำขอที่เหมือนกันจำนวนมากซึ่งความเสี่ยงต่ำ" เท่านั้น
 *
 * กติกาที่บังคับไว้ในหน้าจอ:
 *   — เลือกได้เฉพาะคำขอที่เข้าเกณฑ์ (ระบบตรวจให้แล้ว หรือยอดไม่เกินเพดานและไม่มีสิ่งที่ระบบจับได้)
 *   — คำขอที่ไม่เข้าเกณฑ์แสดงอยู่ในตารางแต่เลือกไม่ได้ พร้อมบอกเหตุผลว่าทำไม
 *   — ต้องผ่านหน้ายืนยันที่แสดงรายการทั้งหมดและคะแนนรวมก่อนกด
 *   — ต้องกรอกเหตุผล และเหตุผลนั้นเข้า Audit Log ทุกรายการ (FR-ADM-37)
 *
 * เพดานยอด 2,000 บาทเป็นค่าตั้งต้นที่รอการยืนยัน (ประเด็นข้อ ช ในเอกสารแผน)
 */

type ScreenState = 'ready' | 'loading' | 'error';
const CHANNELS: Channel[] = ['Shopee', 'Lazada', 'TikTok Shop', 'ร้านขายยา', 'บูธ', 'อื่น ๆ'];

/** เหตุผลที่คำขอหนึ่งใบอนุมัติแบบกลุ่มไม่ได้ — null = ทำได้ */
function blockedReason(c: Claim, amountCap: number): string | null {
  if (!(c.status === 'pending' || c.status === 'overdue' || c.status === 'review')) return 'ตัดสินแล้ว';
  if (c.source === 'auto') return null;
  if (c.anomalies.length > 0) return 'ระบบจับสิ่งที่ต้องดูก่อน';
  if ((c.verifiedAmount ?? c.declaredAmount) > amountCap) return `ยอดเกิน ${amountCap.toLocaleString('th-TH')} บาท`;
  if (c.kind === 'slip' && c.member.claimsRejected >= 3) return 'สมาชิกเคยถูกปฏิเสธ 3 ครั้งขึ้นไป';
  return null;
}

/** รายการที่อนุมัติไม่สำเร็จ พร้อมเหตุผลที่ backend ตอบกลับมา */
export interface BulkFailure {
  claimId: string;
  claimCode: string;
  message: string;
}

export interface BulkReviewScreenProps {
  initialState?: ScreenState;
  /** เพดานยอดที่อนุมัติแบบกลุ่มได้ (บาท) — ค่าจริงต้องมาจากหน้าตั้งค่า */
  amountCap?: number;
  /** คำขอจากชั้น API — ไม่ส่งมาจะใช้ข้อมูลจำลองในไฟล์ */
  claims?: Claim[];
  /**
   * อนุมัติจริงทีละรายการ
   *
   * ⚠️ backend **ไม่มี endpoint อนุมัติเป็นกลุ่ม** — ชั้น API วนยิง PATCH ทีละใบ
   *    จึงล้มเหลวบางรายการได้ ต้องคืนรายการที่ล้มเหลวมาให้หน้าจอแสดง
   *    ห้ามกลืน error แล้วบอกว่าสำเร็จทั้งหมด เพราะเจ้าหน้าที่จะไม่กลับมาตามเก็บ
   *
   * คืน [] = สำเร็จทั้งหมด
   */
  onBulkApprove?: (claims: Claim[], reason: string) => Promise<BulkFailure[]>;
  /** สูตรคำนวณคะแนน — ต้องส่งมาเมื่อต่อ backend จริง (อัตราอยู่ที่ earning policy) */
  pointsForAmount?: (amount: number) => number;
  onRefresh?: () => void;
  refreshing?: boolean;
}

export const BulkReviewScreen: React.FC<BulkReviewScreenProps> = ({
  initialState = 'ready',
  amountCap = 2000,
  claims: claimsProp,
  onBulkApprove,
  pointsForAmount,
  onRefresh,
  refreshing = false,
}) => {
  const toast = useToast();
  const [screenState, setScreenState] = useState<ScreenState>(initialState);
  const [claims, setClaims] = useState<Claim[]>(claimsProp ?? CLAIMS);
  /** รายการที่อนุมัติไม่สำเร็จรอบล่าสุด — แสดงค้างไว้จนกว่าจะลองใหม่ */
  const [failures, setFailures] = useState<BulkFailure[]>([]);
  const [busy, setBusy] = useState(false);

  /* ข้อมูลจาก API มาทีหลัง — ต้องรับทั้งค่าและสถานะหน้าจอ
     ถ้าลืมอัปเดต screenState หน้าจะค้างที่ "กำลังโหลด" ตลอดไป */
  useEffect(() => { if (claimsProp) setClaims(claimsProp); }, [claimsProp]);
  useEffect(() => { setScreenState(initialState); }, [initialState]);

  /* อัตราแลกเปลี่ยนคะแนนอยู่ที่ earning policy ของ division ไม่ใช่ค่าคงที่
     ไม่ส่งมา = ใช้สูตรของข้อมูลจำลอง */
  const points = pointsForAmount ?? pointsFor;
  const [density, setDensity] = useTableDensity();
  const [query, setQuery] = useState('');
  const [channels, setChannels] = useState<Channel[]>([]);
  const [range, setRange] = useState<DateRange>({ from: null, to: null });
  const [onlyEligible, setOnlyEligible] = useState(true);
  const [sort, setSort] = useState<SortState | null>({ columnId: 'channel', direction: 'asc' });
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [visibleCols, setVisibleCols] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const pending = useMemo(
    () => claims.filter((c) => c.status === 'pending' || c.status === 'overdue' || c.status === 'review'),
    [claims],
  );

  const filtered = useMemo(() => pending.filter((c) => {
    if (onlyEligible && blockedReason(c, amountCap) !== null) return false;
    if (channels.length && !channels.includes(c.channel)) return false;
    if (range.from && c.submittedAt.slice(0, 10) < range.from) return false;
    if (range.to && c.submittedAt.slice(0, 10) > range.to) return false;
    if (query && !c.code.includes(query.trim()) && !c.member.name.includes(query.trim())) return false;
    return true;
  }), [pending, onlyEligible, amountCap, channels, range, query]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = (c: Claim): string | number =>
      sort.columnId === 'amount' ? (c.verifiedAmount ?? c.declaredAmount)
        : sort.columnId === 'age' ? c.ageHours
          : sort.columnId === 'channel' ? c.channel
            : c.code;
    return [...filtered].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      return (typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb), 'th')) * dir;
    });
  }, [filtered, sort]);

  const rows = sorted.slice((page - 1) * pageSize, page * pageSize);

  const eligibleSelected = useMemo(
    () => selected.map((id) => claims.find((c) => c.id === id)!).filter((c) => c && blockedReason(c, amountCap) === null),
    [selected, claims, amountCap],
  );

  const totalPoints = eligibleSelected.reduce((s, c) => s + points(c.verifiedAmount ?? c.declaredAmount), 0);
  const totalAmount = eligibleSelected.reduce((s, c) => s + (c.verifiedAmount ?? c.declaredAmount), 0);
  const memberCount = new Set(eligibleSelected.map((c) => c.member.id)).size;

  const columns: Array<Column<Claim>> = useMemo(() => [
    { id: 'code', header: 'รหัสคำขอ', width: 122, pinned: true, required: true, sortable: true, value: (c) => c.code },
    { id: 'member', header: 'สมาชิก', width: 172, value: (c) => c.member.name },
    { id: 'channel', header: 'ช่องทาง', width: 116, sortable: true, value: (c) => c.channel },
    {
      id: 'amount', header: 'ยอด (บาท)', width: 120, numeric: true, sortable: true,
      value: (c) => c.verifiedAmount ?? c.declaredAmount,
      cell: (c) => (c.verifiedAmount ?? c.declaredAmount).toLocaleString('th-TH'),
    },
    {
      id: 'points', header: 'คะแนนที่จะให้', width: 124, numeric: true,
      value: (c) => points(c.verifiedAmount ?? c.declaredAmount),
      cell: (c) => points(c.verifiedAmount ?? c.declaredAmount).toLocaleString('th-TH'),
    },
    { id: 'source', header: 'ที่มา', width: 180, value: (c) => c.source, cell: (c) => <SourceBadge source={c.source} /> },
    { id: 'status', header: 'สถานะ', width: 148, value: (c) => c.status, cell: (c) => <StatusBadge status={c.status} /> },
    { id: 'age', header: 'อายุคำขอ', width: 108, sortable: true, numeric: true, value: (c) => c.ageHours, cell: (c) => `${Math.floor(c.ageHours / 24)} วัน` },
    {
      id: 'eligible', header: 'อนุมัติแบบกลุ่มได้', width: 232,
      value: (c) => blockedReason(c, amountCap) ?? 'ทำได้',
      cell: (c) => {
        const why = blockedReason(c, amountCap);
        return why === null ? (
          <span className="inline-flex items-center gap-1 text-[var(--success-text)]">
            <Icon name="check_circle" size={14} filled />ทำได้
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[var(--warning-text)]">
            <Icon name="block" size={14} filled />
            <span className="bo-truncate">ต้องเปิดดูก่อน — {why}</span>
          </span>
        );
      },
    },
    { id: 'submittedAt', header: 'วันที่ส่ง', width: 118, value: (c) => formatThaiDate(c.submittedAt.slice(0, 10)) },
  ], [amountCap]);

  const visible = visibleCols.length ? visibleCols : columns.map((c) => c.id);

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const out: ActiveFilter[] = [];
    if (onlyEligible) out.push({ id: 'eligible', field: 'แสดงเฉพาะ', display: 'รายการที่อนุมัติแบบกลุ่มได้' });
    if (channels.length) out.push({ id: 'channel', field: 'ช่องทาง', display: channels.join(', ') });
    if (range.from || range.to) out.push({ id: 'range', field: 'ช่วงวันที่', display: `${formatThaiDate(range.from)} – ${formatThaiDate(range.to)}` });
    return out;
  }, [onlyEligible, channels, range]);

  const clearAll = () => { setOnlyEligible(false); setChannels([]); setRange({ from: null, to: null }); setQuery(''); setPage(1); };

  const doExport = (format: 'csv' | 'excel') => {
    const n = exportRows({ columns, rows: sorted, columnIds: visible, filename: exportFilename('รายการอนุมัติแบบกลุ่ม'), format });
    toast.show({ tone: 'success', message: `ส่งออก ${n.toLocaleString('th-TH')} รายการแล้ว — บันทึกใน Audit Log` });
  };

  const runBulk = async (reason?: string) => {
    const before = claims;
    const batch = eligibleSelected;
    const ids = new Set(batch.map((c) => c.id));

    /* อัปเดตหน้าจอทันทีแบบ optimistic — คืนค่าเฉพาะรายการที่ล้มเหลวทีหลัง */
    setClaims((prev) => prev.map((c) => (ids.has(c.id) ? {
      ...c,
      status: 'approved' as QueueStatus,
      decision: {
        at: new Date().toISOString(),
        byId: CURRENT_STAFF_ID,
        pointsGranted: points(c.verifiedAmount ?? c.declaredAmount),
        detail: `อนุมัติแบบกลุ่ม: ${reason ?? ''}`,
      },
    } : c)));
    setSelected([]);
    setConfirmOpen(false);
    setFailures([]);

    /* โหมดข้อมูลจำลอง — ไม่มีชั้น API ให้ยิง จบที่ optimistic เลย */
    if (!onBulkApprove) {
      toast.show({
        tone: 'success',
        message: `อนุมัติแบบกลุ่ม ${ids.size.toLocaleString('th-TH')} รายการ — ให้คะแนนรวม ${totalPoints.toLocaleString('th-TH')} คะแนน`,
        undo: { onUndo: () => setClaims(before), seconds: 12 },
      });
      return;
    }

    setBusy(true);
    let failed: BulkFailure[] = [];
    try {
      failed = await onBulkApprove(batch, reason ?? '');
    } catch (err) {
      /* ล้มทั้งชุด (เช่น เน็ตหลุด) — คืนค่าเดิมทั้งหมด ไม่ปล่อยให้หน้าจอโกหก */
      setClaims(before);
      setBusy(false);
      toast.show({
        tone: 'error',
        message: err instanceof Error ? err.message : 'อนุมัติแบบกลุ่มไม่สำเร็จ กรุณาลองใหม่',
      });
      return;
    }
    setBusy(false);

    if (failed.length === 0) {
      toast.show({
        tone: 'success',
        message: `อนุมัติแบบกลุ่ม ${ids.size.toLocaleString('th-TH')} รายการ — ให้คะแนนรวม ${totalPoints.toLocaleString('th-TH')} คะแนน`,
      });
      return;
    }

    /**
     * สำเร็จบางส่วน — คืนเฉพาะรายการที่ล้มเหลวกลับเป็นสถานะเดิม
     *
     * ⚠️ ห้ามคืนทั้งชุด เพราะรายการที่สำเร็จถูกอนุมัติจริงที่ backend ไปแล้ว
     *    การคืนทั้งชุดจะทำให้หน้าจอไม่ตรงกับความจริง
     */
    const failedIds = new Set(failed.map((f) => f.claimId));
    setClaims((prev) => prev.map((c) => {
      if (!failedIds.has(c.id)) return c;
      return before.find((b) => b.id === c.id) ?? c;
    }));
    setFailures(failed);

    const ok = ids.size - failed.length;
    toast.show({
      tone: 'warning',
      message:
        `อนุมัติสำเร็จ ${ok.toLocaleString('th-TH')} รายการ · ` +
        `ไม่สำเร็จ ${failed.length.toLocaleString('th-TH')} รายการ — ดูรายละเอียดด้านล่างตาราง`,
    });
  };

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-0)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="อนุมัติแบบกลุ่ม"
        count={sorted.length}
        center={<SearchInput value={query} onChange={(v) => { setQuery(v); setPage(1); }} focusOnSlash placeholder="ค้นหารหัสคำขอหรือชื่อสมาชิก" width={320} />}
        end={
          <>
            <DensityToggle value={density} onChange={setDensity} />
            <ColumnPicker columns={columns} persistKey="bulk-review" value={visible} onChange={setVisibleCols} />
            {/* เจ้าหน้าที่หลายคนทำคิวเดียวกัน — ของที่คนอื่นตัดสินไปแล้วจะไม่หายจนกว่าจะโหลดใหม่ */}
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                aria-label="โหลดรายการใหม่"
                title="โหลดรายการใหม่ — ดึงคำขอล่าสุดจากระบบ"
                className="inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-15)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-25)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] disabled:text-[var(--text-disabled)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
              >
                <Icon name="refresh" size={16} className={refreshing ? 'animate-spin' : undefined} />
                {refreshing ? 'กำลังโหลด…' : 'โหลดใหม่'}
              </button>
            )}

            <div className="flex items-center gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-[var(--line-300)] p-[var(--space-05)]">
              <button type="button" onClick={() => doExport('excel')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center gap-1 rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">
                <Icon name="download" size={16} />Excel
              </button>
              <button type="button" onClick={() => doExport('csv')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">CSV</button>
            </div>
          </>
        }
      />

      <div className="flex items-start gap-[var(--space-2)] border-b border-[var(--line-200)] bg-[var(--warning-bg)] px-[var(--space-4)] py-[var(--space-2)]">
        <Icon name="info" size={18} filled className="text-[var(--warning-text)]" />
        <p className="max-w-[110ch] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
          หน้านี้ไม่ใช่ทางลัดของคิวปกติ — ใช้กับคำขอที่เหมือนกันจำนวนมากและความเสี่ยงต่ำเท่านั้น
          สลิปที่ยอดเกิน <strong className="bo-num">{amountCap.toLocaleString('th-TH')}</strong> บาท
          หรือมีสิ่งที่ระบบจับได้ ต้องเปิดดูภาพในคิวปกติก่อนตัดสิน
        </p>
      </div>

      <FilterBar
        active={activeFilters}
        onRemove={(id) => { if (id === 'eligible') setOnlyEligible(false); if (id === 'channel') setChannels([]); if (id === 'range') setRange({ from: null, to: null }); setPage(1); }}
        onClearAll={clearAll}
        currentFilters={{ onlyEligible, channels, range }}
        persistKey="bulk-review"
        resultCount={sorted.length}
      >
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          <input
            type="checkbox"
            checked={onlyEligible}
            onChange={(e) => { setOnlyEligible(e.target.checked); setPage(1); }}
            className="h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
          />
          แสดงเฉพาะรายการที่อนุมัติแบบกลุ่มได้
        </label>

        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          ช่องทาง
          <select
            multiple
            size={1}
            value={channels}
            onChange={(e) => { setChannels([...e.target.selectedOptions].map((o) => o.value as Channel)); setPage(1); }}
            className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-2)] text-[length:var(--text-form)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
          >
            {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        <DateRangePicker value={range} onChange={(r) => { setRange(r); setPage(1); }} />
      </FilterBar>

      <DataTable<Claim>
        columns={columns.filter((c) => visible.includes(c.id))}
        rows={rows}
        rowKey={(c) => c.id}
        caption={`รายการที่รอตรวจ ${sorted.length} รายการ — เลือกได้เฉพาะรายการที่อนุมัติแบบกลุ่มได้`}
        state={screenState}
        rowCountHint={Math.min(pageSize, 14)}
        errorMessage="โหลดรายการไม่สำเร็จ — เชื่อมต่อเซิร์ฟเวอร์ไม่ได้"
        onRetry={() => setScreenState('ready')}
        emptyState={
          activeFilters.length || query ? (
            <EmptyState kind="no-results" title="ไม่พบรายการที่ตรงกับตัวกรอง" action={{ label: 'ล้างตัวกรองทั้งหมด', onClick: clearAll }} />
          ) : (
            <EmptyState kind="queue-clear" title="ไม่มีรายการที่อนุมัติแบบกลุ่มได้" description="คำขอที่ค้างอยู่ต้องเปิดดูภาพก่อนตัดสินทั้งหมด" />
          )
        }
        selectable
        selectedKeys={selected}
        onSelectionChange={(keys) => {
          /* กันการเลือกรายการที่ไม่เข้าเกณฑ์ที่ระดับหน้าจอ */
          setSelected(keys.filter((id) => {
            const c = claims.find((x) => x.id === id);
            return c ? blockedReason(c, amountCap) === null : false;
          }));
        }}
        sort={sort}
        onSortChange={setSort}
        density={density}
        persistKey="bulk-review"
        isFlagged={(c) => blockedReason(c, amountCap) !== null}
        className="min-h-0 flex-1 rounded-none border-0"
      />

      <BulkActionBar
        selectedKeys={selected}
        totalMatching={sorted.length}
        onSelectAllMatching={() => setSelected(sorted.filter((c) => blockedReason(c, amountCap) === null).map((c) => c.id))}
        onClear={() => setSelected([])}
        actions={[
          {
            id: 'review',
            label: `ตรวจทานก่อนอนุมัติ (${totalPoints.toLocaleString('th-TH')} คะแนน)`,
            icon: 'fact_check',
            tone: 'primary',
            /* ปิดระหว่างกำลังยิง — กันกดซ้ำแล้วอนุมัติซ้อน เพราะยิงทีละใบใช้เวลาหลายวินาที */
            disabled: eligibleSelected.length === 0 || busy,
            consequence: 'เปิดหน้ายืนยันที่แสดงรายการทั้งหมดและคะแนนรวม',
            onRun: () => setConfirmOpen(true),
          },
          { id: 'export-sel', label: 'ส่งออกที่เลือก', icon: 'download', onRun: () => doExport('excel') },
        ]}
      />

      {/**
        * รายการที่อนุมัติไม่สำเร็จ
        *
        * ⚠️ ต้องแสดงค้างไว้ ไม่ใช่แค่ toast ที่หายไปใน 8 วินาที
        *    เพราะ backend ไม่มี endpoint กลุ่ม ชั้น API จึงยิงทีละใบและล้มบางใบได้
        *    ถ้าไม่บอกว่าใบไหนล้ม เจ้าหน้าที่จะคิดว่าจบแล้วและไม่กลับมาตามเก็บ
        */}
      {failures.length > 0 && (
        <div
          role="alert"
          className="flex flex-col gap-[var(--space-2)] border-b border-[var(--error-border)] bg-[var(--error-bg)] px-[var(--space-4)] py-[var(--space-3)]"
        >
          <div className="flex items-center gap-[var(--space-2)]">
            <Icon name="error" size={18} filled className="text-[var(--error-text)]" />
            <strong className="text-[length:var(--text-form)] text-[var(--error-text)]">
              อนุมัติไม่สำเร็จ {failures.length.toLocaleString('th-TH')} รายการ — ยังอยู่ในคิวตามเดิม
            </strong>
          </div>
          <ul className="flex flex-col gap-[var(--space-05)] pl-[var(--space-6)]">
            {failures.map((f) => (
              <li key={f.claimId} className="text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
                <strong className="bo-num" style={{ textAlign: 'left' }}>{f.claimCode}</strong> — {f.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Pagination page={page} pageSize={pageSize} totalItems={sorted.length} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} pageSizeOptions={[50, 100, 200]} />

      {/* หน้ายืนยัน — แสดงรายการทั้งหมดก่อนกด เพราะอนุมัติผิดเป็นกลุ่มแก้ยาก */}
      <ConfirmDialog
        open={confirmOpen}
        title={`ยืนยันอนุมัติ ${eligibleSelected.length.toLocaleString('th-TH')} คำขอ`}
        description={
          <div className="flex flex-col gap-[var(--space-25)]">
            <dl className="grid grid-cols-3 gap-[var(--space-2)] rounded-[var(--radius-sm)] bg-[var(--blue-050)] p-[var(--space-25)]">
              <div>
                <dt className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">คะแนนรวมที่จะให้</dt>
                <dd className="bo-num text-[length:var(--text-title-sm)] font-[var(--weight-bold)] text-[var(--blue-700)]" style={{ textAlign: 'left' }}>{totalPoints.toLocaleString('th-TH')}</dd>
              </div>
              <div>
                <dt className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">ยอดรวม</dt>
                <dd className="bo-num text-[length:var(--text-title-sm)] font-[var(--weight-bold)] text-[var(--ink-900)]" style={{ textAlign: 'left' }}>{totalAmount.toLocaleString('th-TH')} บาท</dd>
              </div>
              <div>
                <dt className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">จำนวนสมาชิก</dt>
                <dd className="bo-num text-[length:var(--text-title-sm)] font-[var(--weight-bold)] text-[var(--ink-900)]" style={{ textAlign: 'left' }}>{memberCount.toLocaleString('th-TH')} คน</dd>
              </div>
            </dl>

            <div className="max-h-[220px] overflow-auto rounded-[var(--radius-sm)] border border-[var(--line-200)]">
              <table className="w-full text-[length:var(--text-table)] leading-[var(--leading-table)]">
                <caption className="sr-only">รายการที่จะอนุมัติทั้งหมด</caption>
                <thead className="sticky top-0 bg-[var(--surface-100)]">
                  <tr>
                    <th scope="col" className="px-[var(--space-2)] py-[var(--space-1)] text-left text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]">รหัสคำขอ</th>
                    <th scope="col" className="px-[var(--space-2)] py-[var(--space-1)] text-left text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]">สมาชิก</th>
                    <th scope="col" className="px-[var(--space-2)] py-[var(--space-1)] text-right text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]">ยอด</th>
                    <th scope="col" className="px-[var(--space-2)] py-[var(--space-1)] text-right text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]">คะแนน</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibleSelected.map((c) => (
                    <tr key={c.id} className="border-t border-[var(--line-200)]">
                      <td className="bo-num px-[var(--space-2)] py-[var(--space-1)]" style={{ textAlign: 'left' }}>{c.code}</td>
                      <td className="px-[var(--space-2)] py-[var(--space-1)]">{c.member.name}</td>
                      <td className="bo-num px-[var(--space-2)] py-[var(--space-1)]">{(c.verifiedAmount ?? c.declaredAmount).toLocaleString('th-TH')}</td>
                      <td className="bo-num px-[var(--space-2)] py-[var(--space-1)]">{points(c.verifiedAmount ?? c.declaredAmount).toLocaleString('th-TH')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
              การอนุมัติแบบกลุ่มถูกบันทึกใน Audit Log ทุกรายการพร้อมเหตุผลที่กรอก
              และย้อนได้ภายใน 12 วินาทีหลังกดผ่านปุ่ม “เลิกทำ”
            </p>
          </div>
        }
        confirmLabel={`อนุมัติทั้ง ${eligibleSelected.length} รายการ`}
        requireReason
        reasonLabel="เหตุผลที่อนุมัติแบบกลุ่ม"
        reasonPresets={[
          'ระบบตรวจกับแพลตฟอร์มผ่านทั้งหมด',
          'ยอดต่ำและช่องทางเชื่อถือได้',
          'เคลียร์งานค้างที่ใกล้เกิน SLA',
        ]}
        onConfirm={runBulk}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default BulkReviewScreen;

/* ============================================================================
   รองรับ:
   FR-ADM-13  อนุมัติคำขอแบบกลุ่ม — จำกัดเฉพาะรายการที่เข้าเกณฑ์ พร้อมหน้ายืนยัน
              ที่แสดงรายการทั้งหมด คะแนนรวม ยอดรวม และจำนวนสมาชิก
   FR-ADM-10  รายการที่รอตรวจ (ใช้ข้อมูลชุดเดียวกับคิวหลัก)
   FR-ADM-12  รายการที่ระบบจับสิ่งผิดปกติถูกกันออกจากการเลือกโดยอัตโนมัติ
   FR-ADM-14  แสดงอายุคำขอเพื่อให้เคลียร์งานใกล้เกิน SLA ได้ก่อน
   FR-ADM-37  ทุกรายการที่อนุมัติแบบกลุ่มเข้า Audit Log พร้อมเหตุผลที่กรอก
   FR-ADM-39  การส่งออกถูกบันทึกและแจ้งผู้ใช้
   ประเด็นค้าง: ข้อ ช — เพดานยอด 2,000 บาทและเกณฑ์ "สมาชิกเคยถูกปฏิเสธ 3 ครั้ง"
   เป็นค่าตั้งต้นที่ผมเสนอ ต้องยืนยันก่อนใช้งานจริง
   ============================================================================ */
